import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Columns3, X } from 'lucide-react';

/**
 * "More columns": a dropdown of tick boxes, grouped, for the columns a table does not show by
 * default. `chosen` is in the order the reader ticked them, which is the order they are appended.
 *
 * groups: [{ label, columns: [{ key, label }] }]
 */
const ColumnPicker = ({ groups, chosen, onChange }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const buttonRef = useRef(null);

  // Escape closes only this popover, and only while it is open (see the dialog keyboard model);
  // a click anywhere else closes it too.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const toggle = (key) => onChange(chosen.includes(key) ? chosen.filter((k) => k !== key) : [...chosen, key]);
  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
          open ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Columns3 className="w-4 h-4" />
        More columns
        {chosen.length > 0 && (
          <span className="min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-semibold leading-none">
            {chosen.length}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose more columns"
          className="absolute left-0 top-full mt-2 z-30 w-[min(20rem,calc(100vw-2rem))] bg-white border border-gray-200 rounded-xl shadow-lg flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Add columns to the table</span>
            <button type="button" onClick={close} aria-label="Close" className="p-1 -mr-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-[50vh] overflow-y-auto px-4 py-3 space-y-4">
            {groups.map((g) => (
              <fieldset key={g.label}>
                <legend className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">{g.label}</legend>
                <div className="space-y-0.5">
                  {g.columns.map((c) => (
                    <label key={c.key} className="flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={chosen.includes(c.key)}
                        onChange={() => toggle(c.key)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={chosen.length === 0}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-300"
            >
              Clear all
            </button>
            <button type="button" onClick={close} className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnPicker;
